import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConfigManager from '../managers/ConfigManager';
import ISpineAnimConfig from '../managers/contracts/configurations/spine_anim/ISpineAnimConfig';
import IAnim from '../managers/contracts/animation/IAnim';
import ISpineAnimStateConfig from '../managers/contracts/configurations/spine_anim/ISpineAnimStateConfig';
import IChainingSpineAnimConfig from '../managers/contracts/configurations/spine_anim/IChainingSpineAnimConfig';
import { IAnimationStateListener, ISkeletonData, Spine } from 'pixi-spine';
import ConsoleUtil from '../utils/ConsoleUtil';
import ISpineCustomOverrideData from '../managers/contracts/configurations/spine_anim/ISpineCustomOverrideData';
import Layout from '../utils/Layout';
import SpineUtils from '../utils/SpineUtils';
import { Tween } from '../Tween';
import App from '../App';
import GameEvents from '../const/GameEvents';
import { AttachmentType } from 'pixi-spine';
import Configs from '../../../src/vendorConfigs/Configs';

export default class SpineAnimation extends Spine implements IAnim<ISpineAnimConfig> {
    public animConfig: ISpineAnimConfig;
    private isInitialCalculationDone = false;
    private initialValue: number;
    private secondaryValue: number;
    constructor(animJson: ISkeletonData, name: string, config: ISpineAnimConfig) {
        super(animJson);

        // Detecting spine masks
        const masks: string[] = [];
        animJson.skins.forEach(skin => {
            skin.attachments.forEach(attachment => {
                Object.keys(attachment).forEach(key => {
                    if (attachment[key].type == AttachmentType.Clipping) {
                        masks.push(attachment[key].name);
                    }
                })
            })
        })

        if (masks.length > 0) {
            ConsoleUtil.warn(`[SPINE ANIMATION] - animation '${name}' contains ${masks.length} mask(s): ${masks}. This will cause performance issues! Path: ${App.assets.resources[`spine~${name}~json`].url}`);
        }

        this.animConfig = config;
        if (this.animConfig) {
            if (
                this.animConfig.states?.length !==
                this.stateData.skeletonData.animations.length
            ) {
                ConsoleUtil.warn(
                    `[SPINE ANIMATION] -> animation <${name}> decelerated states in game config doesn't have the same length as skeleton data animations or missing configuration declaration`,
                    {
                        [`${name} animation game-config`]:
                            this.animConfig.states,
                        [`${name} animation skeleton-data-animations`]:
                            this.stateData.skeletonData.animations.map(
                                (data) => data.name
                            ),
                    }
                );
            }
        } else {
            ConsoleUtil.warn(
                `[SPINE ANIMATION] -> animation <${name}> Has not a configuration in SpineAnimationConfig`,
                Configs.spineAnimations
            );
        }

        this._visible = this.animConfig.visible;
    }

    public calculateDimensions(
        customOverrideData: ISpineCustomOverrideData = this.animConfig
            .customOverrideData
    ) {
        let orientationOverrideData;

        if (Layout.isLandscape()) {
            orientationOverrideData = customOverrideData?.landscape;
        } else {
            orientationOverrideData = customOverrideData?.portrait;
        }

        if (this.initialValue) {
            for (const prop in orientationOverrideData) {
                if (prop === "parentMaskDimensions") {
                    for (const maskProp in orientationOverrideData[prop]) {
                        this.parent.mask[maskProp] = this.initialValue;
                    }
                }
            }
        } else {
            for (const prop in orientationOverrideData) {
                if (prop === "parentMaskDimensions") {
                    for (const maskProp in orientationOverrideData[prop]) {
                        if (!this.isInitialCalculationDone) {
                            this.parent.mask[maskProp] +=
                                orientationOverrideData[prop][maskProp] === 0
                                    ? 0
                                    : (this.parent.mask[maskProp] *
                                          orientationOverrideData[prop][
                                              maskProp
                                          ]) /
                                      100;
                            this.initialValue = this.parent.mask[maskProp];
                            this.isInitialCalculationDone = true;
                        }
                    }
                } else {
                    this[prop] += orientationOverrideData[prop];
                }
            }
        }
    }

    public recalculateDimensions(
        customOverrideData: ISpineCustomOverrideData = this.animConfig
            .customOverrideData
    ) {
        let orientationOverrideData;

        if (Layout.isLandscape()) {
            orientationOverrideData = customOverrideData?.landscape;
        } else {
            orientationOverrideData = customOverrideData?.portrait;
        }
        if (this.secondaryValue) {
            for (const prop in orientationOverrideData) {
                if (prop === "parentMaskDimensions") {
                    for (const maskProp in orientationOverrideData[prop]) {
                        this.parent.mask[maskProp] = this.secondaryValue;
                    }
                } else {
                    this[prop] -= orientationOverrideData[prop];
                }
            }
        } else {
            for (const prop in orientationOverrideData) {
                if (prop === "parentMaskDimensions") {
                    for (const maskProp in orientationOverrideData[prop]) {
                        if (this.isInitialCalculationDone) {
                            this.parent.mask[maskProp] +=
                                orientationOverrideData[prop][maskProp] === 0
                                    ? 0
                                    : (this.parent.mask[maskProp] *
                                          orientationOverrideData[prop][
                                              maskProp
                                          ]) /
                                      100;
                            this.secondaryValue = this.parent.mask[maskProp];
                        }
                    }
                } else {
                    this[prop] -= orientationOverrideData[prop];
                }
            }
        }

        this.calculateDimensions();
    }

    public playAsync(
        animState = this.animConfig.defaultState,
        timeScale?: number,
        loop: boolean = this.getStateConfig(animState || this.animConfig.defaultState)?.loop,
        from: number = 0
    ): Promise<boolean> {
        return new Promise<boolean>((res) => {
            const listener = {
                complete: () => {
                    this.state.removeListener(listener);
                    res(true);
                },
            };

            this.addOnCompleteListener(listener);
            this.play(animState, loop, timeScale, from);
        });
    }
    public playAsyncChainingStates(
        animChainingState: string,
        timeScale: number = 1
    ): Promise<boolean> {
        return new Promise<boolean>((res) => {
            let listener = {
                complete: () => {
                    this.state.removeListener(listener);
                    res(true);
                },
            };

            this.addOnCompleteListener(listener);
            this.playChainingStates(animChainingState, timeScale, false);
        });
    }

    protected onCompleteOnce(callback) {
        let listener = {
            complete: () => {
                this.state.removeListener(listener);
                callback();
            }
        };

        this.addOnCompleteListener(listener);
    }

    private addOnCompleteListener(listener: IAnimationStateListener) {
        // Removing the onComplete listeners.
        // This is needed, because SOMETIMES the game hangs, because some onComplete is not resolved.
        this.state.listeners.forEach(oldListener => {
            if (oldListener.complete) {
                oldListener.complete(undefined);
            }
        })

        this.state.addListener(listener);
    }

    /**
     * return the animation duration in sec
     * @param animState the name of the desired animation
     * @returns number
     */
    public getAnimDuration(animState): number {
        return this.skeleton.data.findAnimation(animState).duration;
    }

    /**
     * Play spine animation
     * @param animState: state for current animation witch is defined in-game config
     * @param loop: default is get value from config
     * @param trackTime: move playHead to the given seconds
     * @returns void
     */
    public playFrom(
        animState: string = this.animConfig.defaultState,
        loop: boolean = this.getStateConfig(animState).loop,
        trackTime: number = 0
    ): void {
        if (animState && this.state.hasAnimation(animState)) {
            this.state.setAnimation(0, animState, loop).trackTime = trackTime;
        }
        
    }

    /**
     * Play spine animation
     * @param animState: state for current animation witch is defined in-game config
     * @param loop: default is get value from config
     * @returns void
     */
     public play(
        animState = this.animConfig.defaultState,
        loop = this.getStateConfig(animState || this.animConfig.defaultState)?.loop,
        timeScale: number = 1,
        from: number = 0
    ): void {
        timeScale *= App.instance.timeScale;
        animState = this.parsePtAnimPlay(animState);
        if (animState && this.state.hasAnimation(animState)) {
            this.clearState();
            if (timeScale) {
                this.state.timeScale = timeScale;
            } else {
                let stateConfig = this.getStateConfig(animState);
                if (stateConfig && stateConfig.timeScale) {
                    this.state.timeScale = stateConfig.timeScale;
                } else {
                    this.state.timeScale = 1;
                }
            }
            this.show();
            this.state.setAnimation(0, animState, loop).trackTime = from
        } else {
            if (this.animConfig?.states?.length === 1) {
                this.clearState();
                if (timeScale) {
                    this.state.timeScale = timeScale;
                } else {
                    this.state.timeScale = 1;
                }
                this.show();
                this.state.setAnimation(
                    0,
                    this.animConfig.states[0].name,
                    loop
                ).trackTime = from;
            } else {
                ConsoleUtil.warn(
                    `[SPINE ANIMATION] -> animation state <${animState}> is missing from <${this.animConfig.name}> animation! `,
                    {
                        [`${this.animConfig.name} animation states`]:
                            this.animConfig.states,
                    }
                );
            }
        }

        if (this.animConfig.scaleMultiplier && Array.isArray(this.animConfig.scaleMultiplier)) {
            SpineUtils.applyConfigScaleMultiplier(this, this.scale);
        }
    }
    
    private parsePtAnimPlay = (animState: string) => {
        let result = animState;
        if (animState) {
            if (Layout.isLandscape()) {
                if (animState.includes(`_pt`) && this.state.hasAnimation(`${animState.split("_pt")[0]}`)) {
                    result = animState.split("_pt")[0];
                }
            } else {
                if (this.state.hasAnimation(`${animState}_pt`)) {
                    result = `${animState}_pt`;
                }
            }
        }

        return result
    }

    public pause(): void {
        this.autoUpdate = false;
    }
    public resume(): void {
        this.autoUpdate = true;
    }

    public setAnimationSkin(skinName: string){
        this.skeleton.setSkinByName(skinName);
    }    

    public getStateConfig(state: string): ISpineAnimStateConfig | undefined {
        return this.animConfig.states.find((data) => data.name === state);
    }
    public playAndHide(
        animState = this.animConfig.defaultState,
        timeScale: number = 1
    ) {
        this.playAsync(animState, timeScale).then(() => {
            this.stop(true);
        });
    }

    public hasChainingAnim(chainingAnim: string): boolean {
        return Boolean(this.animConfig.chainingStates.find(data => data.name === chainingAnim))
    }

    public clearState() {
        this.state.clearTracks();
        this.skeleton.setToSetupPose();
        this.lastTime = null;
    }


    public playChainingAnim(
        animName: string,
        timeScale: number = 1
    ): Promise<boolean> {
        return new Promise<boolean>((res) => {

            let chainingAnimConfig = this.getChainingAnimConfig(animName);

            timeScale *= App.instance.timeScale;
            if (timeScale) {
                this.state.timeScale = timeScale;
            } else {
                let stateConfig = this.getStateConfig(animName);
                if (stateConfig && stateConfig.timeScale) {
                    this.state.timeScale = stateConfig.timeScale;
                } else {
                    this.state.timeScale = 1;
                }
            }
            if (chainingAnimConfig) {
                let index = chainingAnimConfig.states.indexOf(
                    chainingAnimConfig.entryPoint
                );
                if (index < 0) {
                    ConsoleUtil.warn(
                        `[SPINE ANIMATION] -> Spine anim <${this.animConfig.name}> animation state <${chainingAnimConfig.entryPoint}> is missing from <${chainingAnimConfig.states}> animation! `
                    );
                } else {
                    this.chainLoop(animName, 0);
                    App.dispatcher.once(GameEvents.CHAINING_ANIMATION_COMPLETE, () => {
                        if (chainingAnimConfig.hideOnComplete) {
                            this.stop(true);
                            this.state.timeScale = timeScale;
                        }
                        res(true);
                    })
                }
            } else {
                return this.startAnim(animName, this.getStateConfig(animName).loop);
            }
        });
    }

    protected chainLoop(chainingAnimName: string, i: number): void/*Promise<boolean>*/ {
        
        let chainingAnimConfig = this.getChainingAnimConfig(chainingAnimName);
        if (i < chainingAnimConfig.states.length -1) {
            this.startAnim(chainingAnimConfig.states[i]).then(() => {
                this.chainLoop(chainingAnimName, ++i);
            });
        } else {
            /*return*/ this.startAnim(chainingAnimConfig.states[i], chainingAnimConfig.hasLoopStateOnComplete);
            App.dispatcher.emit(GameEvents.CHAINING_ANIMATION_COMPLETE);
        }
    }

    protected startAnim(name: string, loop: boolean = false): Promise<boolean> {
        return new Promise<boolean>((res) => {
            this.clearState();
            this.state.setAnimation(0, name, loop).trackTime = 0;
            let dur = this.skeleton.data.findAnimation(name).duration;
            Tween.delayedCall(dur, () => {
                res(true);
            })
        });
    }


    /**
     * name
     */
    public playChainingStates(
        chainingAnim: string,
        timeScale?: number,
        loop?: boolean
    ) {
        let chainingAnimConfig = this.getChainingAnimConfig(chainingAnim);
        if (chainingAnimConfig) {
            let index = chainingAnimConfig.states.indexOf(
                chainingAnimConfig.entryPoint
            );
            if (index < 0) {
                ConsoleUtil.warn(
                    `[SPINE ANIMATION] -> Spine anim <${this.animConfig.name}> animation state <${chainingAnimConfig.entryPoint}> is missing from <${chainingAnimConfig.states}> animation! `
                );
            } else {
                this.clearState();
                timeScale = this.getChainingAnimTimeScaleProp(
                    chainingAnimConfig,
                    timeScale
                );
                loop = this.getChainingAnimLoopProp(loop, chainingAnimConfig);
                chainingAnimConfig.states.forEach((state, i, arr) => {
                    if (loop) {
                        this.state.setAnimation(i, state, false).trackTime = 0;
                        this.state.timeScale = timeScale;
                        if (i >= arr.length - 1) {
                            this.onCompleteOnce(() => {
                                this.playChainingStates(
                                    chainingAnim,
                                    timeScale,
                                    loop
                                );
                            });
                        }
                    } else {
                        if (chainingAnimConfig.hasLoopStateOnComplete) {
                            if (i === chainingAnimConfig.states.length - 1) {
                                this.state.setAnimation(
                                    i,
                                    chainingAnimConfig.loopStateOnComplete,
                                    true
                                ).trackTime = 0;
                            } else {
                                this.state.setAnimation(i, state, false).trackTime = 0;
                            }
                            this.state.timeScale = timeScale;
                        } else if (
                            chainingAnimConfig.hasStaticStateOnComplete
                        ) {
                            if (i === chainingAnimConfig.states.length - 1) {
                                this.state.setAnimation(i, state, false).trackTime = 0;
                                this.onCompleteOnce(() => this.showStatic());
                            } else {
                                this.state.setAnimation(i, state, false).trackTime = 0;
                            }
                            this.state.timeScale = timeScale;
                        } else if (chainingAnimConfig.hideOnComplete) {
                            this.state.setAnimation(i, state, false).trackTime = 0;
                            this.onCompleteOnce(() => this.stop(true));
                            this.state.timeScale = timeScale;
                        } else {
                            this.state.setAnimation(i, state, false).trackTime = 0;
                            this.state.timeScale = timeScale;
                        }
                    }
                });

                this.show();
            }
        } else {
            ConsoleUtil.warn(
                `[SPINE ANIMATION] -> animation chaining state <${chainingAnim}> is missing from chaining states animation configurations! `,
                {
                    resolve: [
                        "use spine animation configuration file and set chaining animation configuration",
                        "use generateChainingAnimation method on SpineAnimation class",
                    ],
                }
            );
        }
    }

    /**
    name
     */
    public getChainingAnimConfig(
        chainingAnim: string
    ): IChainingSpineAnimConfig {
        return this.animConfig.chainingStates?.find(
            (data) => data.name === chainingAnim
        );
    }

    protected getChainingAnimLoopProp(
        loop: boolean,
        config: IChainingSpineAnimConfig
    ): boolean {
        let result = false;
        if (loop === null || loop === undefined) {
            if (config.loop) {
                result = true;
            }
        } else {
            result = loop;
        }

        return result;
    }

    protected getChainingAnimTimeScaleProp(
        config: IChainingSpineAnimConfig,
        timeScale: number = 1
    ): number {
        let result = 1;
        if (!timeScale) {
            if (config?.timeScale) {
                result = config.timeScale;
            }
        } else {
            result = timeScale;
        }

        return result * App.instance.timeScale;
    }

    public stop(hide = false) {
        this.state.setEmptyAnimation(0, 0);
        if (hide) {
            this.hide();
        }
    }

    public hide() {
        this._visible = false;
    }

    public show() {
        this._visible = true;
    }

    public showStatic(staticAnim?: string, stopOnTime?: number) {
        if (staticAnim && this.state.hasAnimation(staticAnim)) {
            let stopTime = 0;
            if (stopOnTime) {
                stopTime = stopOnTime;
            } else if (this.animConfig.stopTimeOnStatic) {
                stopTime = this.animConfig.stopTimeOnStatic;
            } else {
            }
            this.show();
            this.play(staticAnim, false);
            // this.state.tracks[0].animationStart = stopTime;
            this.state.tracks[0].animationEnd = stopTime;
        } else if (
            this.animConfig.staticState &&
            this.state.hasAnimation(this.animConfig.staticState)
        ) {
            let stopTime = 0;
            if (stopOnTime) {
                stopTime = stopOnTime;
            } else if (this.animConfig.stopTimeOnStatic) {
                stopTime = this.animConfig.stopTimeOnStatic;
            } else {
            }
            this.show();
            this.play(this.animConfig.staticState, false);
            // this.state.tracks[0].animationStart = stopTime;
            this.state.tracks[0].animationEnd = stopTime;
        }
    }
}
